<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    insuranceCompany: {
        type: [String, Number],
        default: ''
    },
    padding: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const insuranceManagerData = ref([]);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    const query = (searchQuery.value || '').trim().toLowerCase();
    const qDigits = query.replace(/\D/g, '');

    filteredOptions.value = (insuranceManagerData.value || []).filter((option) => {
        const name = (option.value || '').toLowerCase();
        const contact = (option.manager_contact || '').toLowerCase();
        const fax = (option.manager_fax || '').toLowerCase();

        const contactDigits = (option.manager_contact || '').replace(/\D/g, '');
        const faxDigits = (option.manager_fax || '').replace(/\D/g, '');

        return name.includes(query) || contact.includes(query) || fax.includes(query) || (!!qDigits && (contactDigits.includes(qDigits) || faxDigits.includes(qDigits)));
    });
};

const searchinsuranceManager = async () => {
    const insurance_company = commonCode('INSURANCE_COMPANY').value;
    const referralName = props.insuranceCompany;
    const query = referralName ? `?referralName=${encodeURIComponent(referralName)}` : '';
    const config = { method: 'get' };
    try {
        const { execute } = useAxios(`/api/referral/insurance${query}`, config, {
            immediate: false,
            onSuccess(response) {
                const newList = response.data.data.map((item, idx) => {
                    const insurance_company_val = insurance_company.find((ft) => ft.value === item.referral_name);
                    return {
                        key: [item.referral_name, item.charge, item.charge_phone, item.fax, idx].map((val) => String(val ?? '')).join('||'),
                        value: item.charge || '',
                        company: item.referral_name || '',
                        company_name: insurance_company_val.text || '',
                        manager_contact: item.charge_phone || '',
                        manager_fax: item.fax || ''
                    };
                });

                insuranceManagerData.value = newList || [];
                filteredOptions.value = newList || [];
            },
            onError(err) {
                console.error('Error:', err.message);
                insuranceManagerData.value = [];
                filteredOptions.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        insuranceManagerData.value = [];
        filteredOptions.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서 위치 조정
    // if (lengthDiff !== 0) {
    //     cursorPosition = cursorPosition + lengthDiff;
    // }

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value,
        company: '',
        company_name: '',
        manager_contact: '',
        manager_fax: '',
        isSelected: false
    });
};

// 팝업 열기/닫기
const togglePopup = async () => {
    if (isPopupVisible.value) {
        closePopup();
        return;
    }

    await searchinsuranceManager();
    isPopupVisible.value = true;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

// 값 선택
const selectValue = (option) => {
    inputValue.value = option.text;
    //emitUpdate(option.text);

    emit('update:modelValue', option.value);
    emit('input', {
        id: props.inputId,
        value: option.value,
        company: option.company || '',
        company_name: option.company_name || '',
        manager_contact: option.manager_contact || '',
        manager_fax: option.manager_fax || '',
        isSelected: true
    });
    ValidationMsg.value = '';
    closePopup();
};

const clearInput = () => {
    emitUpdate('', 'input');
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filteredOptions.value = insuranceManagerData.value || [];
    }
});

const formatPhoneNumber = (value) => {
    if (!value) return '';

    value = String(value).replace(/[^0-9]/g, '');

    // 휴대폰 번호 (010으로 시작, 3-4-4 형식)
    if (value.startsWith('010')) {
        if (value.length <= 3) return value;
        if (value.length <= 7) return value.slice(0, 3) + '-' + value.slice(3);
        return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }

    // 서울 지역 번호 (02로 시작, 2-3-4 또는 2-4-4 형식)
    if (value.startsWith('02')) {
        if (value.length <= 2) return value;
        if (value.length <= 5) return value.slice(0, 2) + '-' + value.slice(2);
        if (value.length <= 9) return value.slice(0, 2) + '-' + value.slice(2, 5) + '-' + value.slice(5);
        return value.slice(0, 2) + '-' + value.slice(2, 6) + '-' + value.slice(6, 10);
    }

    // 기타 지역 번호 (0으로 시작, 3-3-4 또는 3-4-4 형식)
    if (value.startsWith('0')) {
        if (value.length <= 3) return value;
        if (value.length <= 6) return value.slice(0, 3) + '-' + value.slice(3);
        if (value.length <= 10) return value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6);
        return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }

    // 기본 반환 (규칙에 맞지 않는 경우)
    return value;
};

const formatBirthDate = (date) => {
    if (!date) return '';
    const cleaned = date.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return date;
};
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <input
                v-if="visible"
                type="text"
                :id="inputId"
                v-model="inputValue"
                class="p-inputtext block w-full h-[2.5rem] ps-5 input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': button,
                    'error-border': ValidationMsg,
                    'readonly-Bg': isReadonly
                }"
                :readonly="isReadonly"
                :placeholder="placeholder"
                :required="required"
                @input="onInput"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button mr-[calc(6.5rem)]">&times;</button>

                <button v-if="!isReadonly" @click="togglePopup" class="button-style">검색</button>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <!-- <h3>선택</h3> -->
                    <input v-model="searchQuery" @input="filterOptions" placeholder="보험사 담당자를 검색하세요" class="search-input" style="margin-top: 1rem" />
                    <div class="options-container">
                        <table class="insuranceManager-grid">
                            <thead>
                                <tr>
                                    <th>보험사</th>
                                    <th>담당자</th>
                                    <th>담당자 연락처</th>
                                    <th>담당자 FAX</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="option in filteredOptions" :key="option.key" @click="selectValue(option)" class="popup-item">
                                    <td>{{ option.company_name }}</td>
                                    <td>{{ option.value }}</td>
                                    <td>{{ formatPhoneNumber(option.manager_contact) }}</td>
                                    <td>{{ formatPhoneNumber(option.manager_fax) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.insuranceManager-grid th {
    position: sticky;
    top: 0;
    background-color: #f2f2f2;
    z-index: 10;
}
.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}

/* readonly 상태(input[readonly]) 포커스 시 기본 회색 테두리 유지 */
input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}
.text-right {
    text-align: right;
    padding-right: 6rem;
}

.caution-customer {
    color: #ff0000;
    font-family: 'SCoreDream500';
}

.error-border {
    border: 1px solid red !important;
}
.readonly-Bg {
    background-color: whitesmoke !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 40rem;
    max-height: 80vh;
    text-align: left;
    min-width: 30rem;
    display: flex;
    flex-direction: column;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.insuranceManager-grid {
    width: 100%;
    border-collapse: collapse;
}

.insuranceManager-grid th,
.insuranceManager-grid td {
    border: 0.1rem solid #ddd;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
}

.insuranceManager-grid th {
    background-color: #f2f2f2;
    position: sticky;
    top: 0;
    z-index: 10;
    text-align: center;
}

.insuranceManager-grid td {
    text-align: left;
    padding: 0.3rem 1rem;
}

.insuranceManager-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.insuranceManager-grid th:hover {
    cursor: default;
}

.insuranceManager-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
